import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";
import { EmployerHubLayout } from "../../components/hub/EmployerHubLayout";
import { HubEmptyPanel } from "../../components/hub/HubEmptyPanel";
import { Badge } from "../../components/ui/badge";
import { CalendarClock, ArrowRight, MailCheck, MailOpen } from "lucide-react";

const initialsOf = (name) =>
  (name || "").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

export default function EmployerHubInterviews() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get("/api/employer/hub/interviews")
      .then((res) => setData(res.data))
      .catch(() => setError("Unable to load interview requests"));
  }, []);

  const interviews = data?.interviews || [];

  return (
    <EmployerHubLayout>
      <div data-testid="employer-hub-interviews">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Interviews</h1>
        <p className="mt-2 text-gray-500">Every candidate you&apos;ve asked to meet, in one place.</p>

        {error && (
          <div className="mt-8 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">{error}</div>
        )}

        {!data && !error && (
          <div className="mt-10 space-y-4" data-testid="hub-interviews-loading">
            {[0, 1].map((i) => (
              <div key={i} className="h-24 bg-gray-100 rounded-2xl animate-pulse" />
            ))}
          </div>
        )}

        {data && interviews.length === 0 && (
          <div className="mt-10">
            <HubEmptyPanel
              icon={CalendarClock}
              testid="hub-interviews-empty"
              title="No interview requests yet"
              body="When you request an interview with a candidate, it will be recorded here so you always know who you've reached out to."
            />
          </div>
        )}

        {interviews.length > 0 && (
          <div className="mt-10 space-y-4" data-testid="hub-interviews-list">
            {interviews.map((iv, i) => (
              <div
                key={iv.message_id}
                className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
                data-testid={`interview-row-${i}`}
              >
                <span className="h-12 w-12 rounded-full bg-blue-600 text-white text-sm font-semibold flex items-center justify-center shrink-0">
                  {initialsOf(iv.candidate_name)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-base font-semibold text-gray-900" data-testid={`interview-candidate-name-${i}`}>
                      {iv.candidate_name}
                    </h3>
                    {iv.position_context && (
                      <Badge variant="outline" className="text-blue-700 border-blue-200 bg-blue-50 font-normal">
                        {iv.position_context}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-500" data-testid={`interview-requested-date-${i}`}>
                    Requested {formatDate(iv.requested_at)}
                  </p>
                </div>
                <div className="flex items-center gap-4 sm:gap-6">
                  <span
                    className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                      iv.status === "viewed" ? "text-emerald-700" : "text-gray-500"
                    }`}
                    data-testid={`interview-status-${i}`}
                  >
                    {iv.status === "viewed" ? <MailOpen className="h-3.5 w-3.5" /> : <MailCheck className="h-3.5 w-3.5" />}
                    {iv.status === "viewed" ? "Viewed by candidate" : "Sent"}
                  </span>
                  {iv.assignment_id && (
                    <Link
                      to={`/employers/hub/candidates/${iv.assignment_id}`}
                      className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700"
                      data-testid={`interview-view-candidate-${i}`}
                    >
                      View candidate
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </EmployerHubLayout>
  );
}
